import React, { useState, useEffect } from "react"
import { SearchIcon } from "lucide-react"
import { useHouseholds } from "../../api/household"

interface HouseholdsTabProps {
  communicationId: string
  selectedHouseholds: string[]
  onHouseholdSelectionChange: (selectedIds: string[]) => void
}

const HouseholdsTab: React.FC<HouseholdsTabProps> = ({
  communicationId,
  selectedHouseholds,
  onHouseholdSelectionChange,
}) => {
  const [searchTerm, setSearchTerm] = useState("")

  const { data: households, isLoading, error } = useHouseholds()

  const filteredHouseholds = households
    ? households.filter((household) =>
        household.name.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    : []

  const handleHouseholdToggle = (id: string) => {
    if (selectedHouseholds.includes(id)) {
      onHouseholdSelectionChange(selectedHouseholds.filter((hId) => hId !== id))
    } else {
      onHouseholdSelectionChange([...selectedHouseholds, id])
    }
  }

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      // Select all filtered households
      const allIds = filteredHouseholds.map((household) => household.id)
      onHouseholdSelectionChange(allIds)
    } else {
      // Deselect all
      onHouseholdSelectionChange([])
    }
  }

  const areAllSelected =
    filteredHouseholds.length > 0 &&
    filteredHouseholds.every((household) =>
      selectedHouseholds.includes(household.id),
    )

  return (
    <div className="households-tab">
      <h2 className="tab-heading">Households</h2>
      <p className="tab-subtext">
        Please select all households you would like to include with this report
      </p>

      <div className="search-container">
        <label htmlFor="householdSearch">Search Households</label>
        <div className="search-input-wrapper">
          <SearchIcon size={18} className="search-icon" />
          <input
            id="householdSearch"
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="selected-count">
        {selectedHouseholds.length} households selected
      </div>

      {isLoading ? (
        <div className="loading-container">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p>Loading households...</p>
        </div>
      ) : error ? (
        <div className="error-container">
          <p className="text-danger">
            Error loading households. Please try again.
          </p>
        </div>
      ) : (
        <div className="table-container">
          <table className="households-table">
            <thead>
              <tr>
                <th className="checkbox-column">
                  <input
                    type="checkbox"
                    checked={areAllSelected}
                    onChange={handleSelectAll}
                    disabled={filteredHouseholds.length === 0}
                  />
                </th>
                <th>Select All</th>
              </tr>
            </thead>
            <tbody>
              {filteredHouseholds.length === 0 ? (
                <tr>
                  <td colSpan={2} className="text-center">
                    {searchTerm
                      ? "No households match your search"
                      : "No households available"}
                  </td>
                </tr>
              ) : (
                filteredHouseholds.map((household) => (
                  <tr
                    key={household.id}
                    className={
                      selectedHouseholds.includes(household.id)
                        ? "selected"
                        : ""
                    }
                  >
                    <td className="checkbox-column">
                      <input
                        type="checkbox"
                        checked={selectedHouseholds.includes(household.id)}
                        onChange={() => handleHouseholdToggle(household.id)}
                      />
                    </td>
                    <td>{household.name}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default HouseholdsTab
